import { authorizedFetch } from './api-client';
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export interface Holiday {
  id: string;
  name: string;
  holiday_date: string; // YYYY-MM-DD
  scope: string; // NATIONAL, STATE, MUNICIPAL, CLINIC
  state_code: string;
  city_name: string;
  clinic_id?: string | null;
}

export interface HolidayCreateData {
  name: string;
  holiday_date: string;
  scope: string;
  state_code: string;
  city_name: string;
  clinic_id?: string | null;
}

export interface Municipality {
  id: string;
  ibge_code: string;
  name: string;
  state_code: string;
}

export interface ImportResult {
  year: number;
  created: number;
  skipped: number;
  source: string;
}

export async function fetchHolidays(
  scope?: string,
  state_code?: string,
  city_name?: string,
  year?: number,
  clinic_id?: string,
): Promise<Holiday[]> {
  const params = new URLSearchParams();
  if (scope) params.append('scope', scope);
  if (state_code) params.append('state_code', state_code);
  if (city_name) params.append('city_name', city_name);
  if (year) params.append('year', year.toString());
  if (clinic_id) params.append('clinic_id', clinic_id);

  const response = await authorizedFetch(`${API_BASE_URL}/api/v1/holidays/?${params}`);
  if (!response.ok) throw new Error('Erro ao buscar feriados');
  const data = await response.json();
  return data.items ?? data;
}

export async function createHoliday(data: HolidayCreateData): Promise<Holiday> {
  const response = await authorizedFetch(`${API_BASE_URL}/api/v1/holidays/`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Erro ao criar feriado');
  }
  return response.json();
}

export async function updateHoliday(id: string, data: Partial<HolidayCreateData>): Promise<Holiday> {
  const response = await authorizedFetch(`${API_BASE_URL}/api/v1/holidays/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Erro ao atualizar feriado');
  }
  return response.json();
}

export async function deleteHoliday(id: string): Promise<void> {
  const response = await authorizedFetch(`${API_BASE_URL}/api/v1/holidays/${id}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Erro ao excluir feriado');
  }
}

export async function importHolidays(year: number): Promise<ImportResult> {
  const response = await authorizedFetch(`${API_BASE_URL}/api/v1/holidays/import/${year}`, {
    method: 'POST',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Erro ao importar feriados');
  }
  return response.json();
}

export async function fetchMunicipalities(state_code?: string): Promise<Municipality[]> {
  const params = new URLSearchParams();
  if (state_code) params.append('state_code', state_code);

  const response = await authorizedFetch(`${API_BASE_URL}/api/v1/holidays/municipalities/list?${params}`);
  if (!response.ok) throw new Error('Erro ao buscar municipios');
  return response.json();
}
